import '../../css/freelancer-edit-modal.css';
import React, { useState, useEffect } from 'react';
import { editUserProfile } from '../../services/userService';
import { cropAndResizeProfileImage } from '../../utils/imageUtils';
import { US_STATES } from '../../utils/usStates';
import { getImage } from '../../utils/cloudinarySizing';

import { patchProfile } from '../../store/profileSlice';
import { useSelector } from "react-redux";
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';


const EditFreelancerModal = ({ freelancerData, onClose, handleUpdate, setProfileThumbnail }) => {
    
    const dispatch = useDispatch();
    
    const { profile } = useSelector((state) => state.profile);
    
    const [formData, setFormData] = useState({
        photo_original: '',
        rawImage: null,
        first_name: '',
        last_name: '',
        city: '',
        state: '',
        services: '',
        portfolio_link: '',
        instagram: '',
        facebook: '',
        youtube: '',
        x: '',
    });

    const [previewPhoto, setPreviewPhoto] = useState(null);

    const handleChange = (e, type) => {
        if (type === 'image') {
            const file = e.target.files[0];
            if (file) {
                setFormData({
                    ...formData,
                    photo_original: URL.createObjectURL(file), // preview
                    rawImage: file, // keep original for upload
                });

                cropAndResizeProfileImage(file, (processedBase64) => {
                    setPreviewPhoto(processedBase64);
                });
            }
        } else {
            const { name, value } = e.target;
            setFormData({ ...formData, [name]: value });
        }
    };

    const normalizeUrl = (url) => {
        if (!url) return "";

        let trimmed = url.trim();

        // If starts with http already → leave it
        if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
            return trimmed;
        }

        // If starts with www → add https://
        if (trimmed.startsWith("www.")) {
            return `https://${trimmed}`;
        }

        // If contains a dot "mybiz.com" → assume domain
        if (trimmed.includes(".")) {
            return `https://${trimmed}`;
        }

        // Otherwise return as-is (user typed something random)
        return trimmed;
    };


    const handleSubmit = async () => {
        try {
            const profileForm = new FormData();

            // USER PROFILE DATA
            profileForm.append("first_name", formData.first_name);
            profileForm.append("last_name", formData.last_name);
            profileForm.append("city", formData.city);
            profileForm.append("state", formData.state);

            if (formData.rawImage) {
                profileForm.append("photo_original", formData.rawImage);
            }


            // FREELANCER LINKS ALSO GO HERE IF YOUR BACKEND HANDLES NESTED UPDATE
            profileForm.append(
                "portfolio_link",
                normalizeUrl(formData.portfolio_link)
            );
            profileForm.append(
                "instagram",
                normalizeUrl(formData.instagram)
            );
            profileForm.append(
                "facebook",
                normalizeUrl(formData.facebook)
            );
            profileForm.append(
                "youtube",
                normalizeUrl(formData.youtube)
            );
            profileForm.append(
                "x",
                normalizeUrl(formData.x)
            );


            // 1. Update backend
            const updatedProfile = await editUserProfile(profileForm);


            // 2. Update Redux profile
            dispatch(
                patchProfile({
                    profile: {
                        ...profile,
                        ...updatedProfile,
                    },
                })
            );


            // 3. Update thumbnail globally
            if (updatedProfile.photo_original) {
                setProfileThumbnail(updatedProfile.photo_original);
                localStorage.setItem(
                    "avatar",
                    updatedProfile.photo_original
                );
            }


            // 4. Update public freelancer data
            handleUpdate({
                ...freelancerData,

                // from profile
                first_name: updatedProfile.first_name,
                last_name: updatedProfile.last_name,
                city: updatedProfile.city,
                state: updatedProfile.state,
                photo_original: updatedProfile.photo_original,

                // freelancer fields
                services: formData.services,
                portfolio_link: updatedProfile.freelancer?.portfolio_link,
                instagram: updatedProfile.freelancer?.instagram,
                facebook: updatedProfile.freelancer?.facebook,
                youtube: updatedProfile.freelancer?.youtube,
                x: updatedProfile.freelancer?.x,
            });


            onClose();
            toast.success("Profile updated successfully!");

        } catch (error) {
            console.error(error);
            toast.error("Failed to update profile");
        }
    };

    useEffect(() => {
        if (freelancerData) {
            setFormData({
                photo_original: getImage(freelancerData.photo_original, "avatar_sm") || '/servspace-placeholder-500x500.png',
                rawImage: null,
                first_name: freelancerData.first_name || '',
                last_name: freelancerData.last_name || '',
                city: freelancerData.city || '',
                state: freelancerData.state || '',
                services: freelancerData.services || '',
                portfolio_link: freelancerData.portfolio_link || '',
                instagram: freelancerData.instagram || '',
                facebook: freelancerData.facebook || '',
                youtube: freelancerData.youtube || '',
                x: freelancerData.x || '',
            });
        }
    }, [freelancerData]);

    return (
        <div
            className="freelancer-modal-backdrop"
            onMouseDown={(e) => {
                // If they clicked *directly on the backdrop* (not inside modal)
                // we mark it so mouseup can close it.
                if (e.target === e.currentTarget) {
                    e.currentTarget.dataset.shouldClose = "true";
                }
            }}
            onMouseUp={(e) => {
                // Only close if mouse started AND ended on the backdrop
                if (e.currentTarget.dataset.shouldClose === "true" && e.target === e.currentTarget) {
                    onClose();
                }
                e.currentTarget.dataset.shouldClose = "false";
            }}
        >
            <div className="freelancer-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="freelancer-modal-scroll">
                    <h3 className='freelancer-modal-title'>Edit Profile</h3>

                    {/* Profile Picture */}
                    <div className="form-group">
                        <label>Profile Photo:</label>
                        <div className="profile-preview-container">
                            <img
                                src={previewPhoto || formData.photo_original || '/servspace-placeholder-150x150.png'}
                                alt="Profile"
                                className="profile-image-preview"
                            />
                        </div>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleChange(e, 'image')}
                        />
                    </div>

                    <div className="name-fields-container">
                        <div className="form-group">
                            <label>First Name:</label>
                            <input
                                type="text"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                placeholder="First Name"
                            />
                        </div>

                        <div className="form-group">
                            <label>Last Name:</label>
                            <input
                                type="text"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                placeholder="Last Name"
                            />
                        </div>
                    </div>

                    <div className="name-fields-container">
                        <div className="form-group">
                            <label>City / Town:</label>
                            <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                placeholder="City"
                            />
                        </div>

                        <div className="form-group">
                            <label>State:</label>
                            <select
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                            >
                                <option value="">Select</option>
                                {US_STATES.map((state) => (
                                    <option key={state.value} value={state.value}>
                                        {state.value}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Portfolio Link */}
                    <div className="form-group">
                        <label><i className="fa-solid fa-link"></i> Website:</label>
                        <input
                            type="text"
                            name="portfolio_link"
                            value={formData.portfolio_link}
                            onChange={handleChange}
                            placeholder="Website"
                        />
                    </div>

                    {/* Social Links */}
                    <div className="form-group">
                        <label><i className="fa-brands fa-instagram"></i> Instagram URL:</label>
                        <input
                            type="text"
                            name="instagram"
                            value={formData.instagram}
                            onChange={handleChange}
                            placeholder="Instagram URL"
                        />
                    </div>
                    <div className="form-group">
                        <label><i className="fa-brands fa-facebook"></i> Facebook URL:</label>
                        <input
                            type="text"
                            name="facebook"
                            value={formData.facebook}
                            onChange={handleChange}
                            placeholder="Facebook URL"
                        />
                    </div>
                    <div className="form-group">
                        <label><i className="fa-brands fa-youtube"></i> YouTube URL:</label>
                        <input
                            type="text"
                            name="youtube"
                            value={formData.youtube}
                            onChange={handleChange}
                            placeholder="YouTube URL"
                        />
                    </div>
                    <div className="form-group">
                        <label><i className="fa-brands fa-x-twitter"></i> X (Twitter) URL:</label>
                        <input
                            type="text"
                            name="x"
                            value={formData.x}
                            onChange={handleChange}
                            placeholder="X (Twitter) URL"
                        />
                    </div>

                    {/* Buttons */}
                    <div className="freelancer-modal-buttons">
                        <button className='save-btn' onClick={handleSubmit}>Save</button>
                        <button className='cancel-btn' onClick={onClose}>Cancel</button>
                    </div>
                </div>
            </div>
        </div>

    );
};

export default EditFreelancerModal;
